import { useState } from 'react';
import { ConfirmModal, Modal, useModalRequestClose } from '@safetyhub/ui';
import { BottomSheet } from '../touch';
import { useDeviceLayout } from '../../../hooks/useDeviceLayout';
import { MODAL_LAYER, SUB_MODAL_OVERLAY_BG } from '../../modalLayers';

/** 발견사항 카테고리 목록 */
export const FINDING_CATEGORIES = [
  '추락위험',
  '전기위험',
  '화재위험',
  '충돌위험',
  '끼임위험',
  '낙하물위험',
  '미끄러짐위험',
  '유해물질',
  '소음/진동',
  '부적절한 보호구',
  '작업환경불량',
  '기타',
] as const;

/** 우수사례 카테고리 목록 */
export const BEST_PRACTICE_CATEGORIES = [
  '안전시설 우수',
  '보호구 착용 우수',
  '정리정돈 우수',
  '안전표지 우수',
  '작업환경 우수',
  '안전교육 우수',
  '비상대응 우수',
  '기타',
] as const;

interface AddFindingSheetProps {
  isOpen: boolean;
  onClose: () => void;
  photoId: string;
  onSubmit: (data: {
    category: string;
    description: string;
  }) => Promise<void>;
  isLoading?: boolean;
  /** 우수사례 모드 */
  mode?: 'hazard' | 'best-practice';
}

export function AddFindingSheet({
  isOpen,
  onClose,
  photoId: _photoId,
  onSubmit,
  isLoading = false,
  mode = 'hazard',
}: AddFindingSheetProps) {
  const layout = useDeviceLayout();
  const isBestPractice = mode === 'best-practice';
  const categories = isBestPractice ? BEST_PRACTICE_CATEGORIES : FINDING_CATEGORIES;

  const [category, setCategory] = useState<string>('');
  const [customCategory, setCustomCategory] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  // BottomSheet 분기의 미저장 닫기 확인 노출 여부(아래 requestSheetClose).
  const [showSheetDirtyConfirm, setShowSheetDirtyConfirm] = useState(false);

  // 카테고리가 "기타"인 경우 커스텀 카테고리 사용
  const effectiveCategory = category === '기타' ? customCategory : category;

  const handleSubmit = async () => {
    setError(null);

    // 유효성 검사
    if (!effectiveCategory.trim()) {
      setError('카테고리를 선택하거나 입력해주세요.');
      return;
    }
    if (!description.trim()) {
      setError('설명을 입력해주세요.');
      return;
    }

    try {
      await onSubmit({
        category: effectiveCategory.trim(),
        description: description.trim(),
      });

      // 성공 후 폼 초기화
      resetForm();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : '추가에 실패했습니다.');
    }
  };

  const resetForm = () => {
    setCategory('');
    setCustomCategory('');
    setDescription('');
    setError(null);
    setShowSheetDirtyConfirm(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // 미저장 닫기 보호 — 기준선은 빈 폼(닫을 때마다 resetForm으로 되돌리므로 열 때 늘 비어 있다).
  // customCategory는 분류가 「기타」일 때만 보이고 그때는 category가 이미 비어 있지 않아 따로 세지 않는다.
  const isDirty = category !== '' || description !== '';

  // 모바일/폴드(BottomSheet) 분기의 확인 — 시트는 base Modal이 아니라 base의 isDirty를 쓸 수 없다.
  // 시트가 MODAL_LAYER.root(z-[70]) 세계 안이라 확인은 한 층 위(sub)에 뜬다.
  const requestSheetClose = () => {
    if (isDirty) {
      setShowSheetDirtyConfirm(true);
      return;
    }
    handleClose();
  };

  const title = isBestPractice ? '우수사례 추가' : '발견사항 추가';

  // 폼 내용 (데스크톱/모바일 공통)
  const formContent = (
    <div className="space-y-5">
      {/* 에러 메시지 */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-card text-sm text-red-600">
          {error}
        </div>
      )}

      {/* 카테고리 선택 */}
      <div>
        <label className="block text-sm font-medium text-ink-700 mb-2">
          분류 <span className="text-red-500">*</span>
        </label>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className={`w-full px-3 py-2.5 border border-line rounded-control focus:ring-2 ${isBestPractice ? 'focus:ring-emerald-500 focus:border-emerald-500' : 'focus:ring-brand-500 focus:border-brand-500'} text-sm`}
        >
          <option value="">분류 선택</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>

        {/* 기타 카테고리 직접 입력 */}
        {category === '기타' && (
          <input
            type="text"
            value={customCategory}
            onChange={(e) => setCustomCategory(e.target.value)}
            placeholder="분류를 직접 입력하세요"
            className={`w-full mt-2 px-3 py-2.5 border border-line rounded-control focus:ring-2 ${isBestPractice ? 'focus:ring-emerald-500 focus:border-emerald-500' : 'focus:ring-brand-500 focus:border-brand-500'} text-sm`}
          />
        )}
      </div>

      {/* 설명 입력 */}
      <div>
        <label className="block text-sm font-medium text-ink-700 mb-2">
          {isBestPractice ? '내용' : '설명'} <span className="text-red-500">*</span>
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={isBestPractice ? '우수사례에 대한 설명을 입력하세요' : '위험 상황에 대한 설명을 입력하세요'}
          rows={4}
          className={`w-full px-3 py-2.5 border border-line rounded-control focus:ring-2 ${isBestPractice ? 'focus:ring-emerald-500 focus:border-emerald-500' : 'focus:ring-brand-500 focus:border-brand-500'} text-sm resize-none`}
        />
      </div>

      {/* 버튼 */}
      <div className="flex gap-3 pt-2">
        <CancelButton disabled={isLoading} fallbackClose={requestSheetClose} />
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isLoading || !effectiveCategory.trim() || !description.trim()}
          className={`flex-1 py-3 px-4 text-white rounded-card-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${
            isBestPractice
              ? 'bg-emerald-600 hover:bg-emerald-700'
              : 'bg-brand-600 hover:bg-brand-700'
          }`}
        >
          {isLoading ? '추가 중...' : '추가'}
        </button>
      </div>
    </div>
  );

  // 데스크톱: base 모달. 추가 요청 중에는 닫기 수단을 전부 막는다(종전엔 바깥 클릭·✕로 도중에
  // 닫혔다 — 취소 버튼만 disabled였다).
  if (layout === 'desktop') {
    return (
      <Modal
        open={isOpen}
        onClose={handleClose}
        title={title}
        size="md"
        overlayZClassName={MODAL_LAYER.root}
        closeLocked={isLoading}
        isDirty={isDirty}
      >
        {formContent}
      </Modal>
    );
  }

  // 모바일/폴드: BottomSheet — 바깥 탭·아래로 끌기·취소가 전부 requestSheetClose로 모인다.
  return (
    <>
      <BottomSheet
        isOpen={isOpen}
        onClose={requestSheetClose}
        title={title}
        height="75vh"
      >
        <div className="px-5 pb-6">
          {formContent}
        </div>
      </BottomSheet>
      {/* 문구·danger 규약은 base Modal의 dirty 확인과 같다(Enter가 「계속 편집」). */}
      {isOpen && showSheetDirtyConfirm && (
        <ConfirmModal
          open
          title="닫기 확인"
          body="저장하지 않은 변경사항이 있습니다. 닫으면 변경사항이 사라집니다."
          confirmLabel="닫기"
          cancelLabel="계속 편집"
          danger
          overlayZClassName={MODAL_LAYER.sub}
          overlayBgClassName={SUB_MODAL_OVERLAY_BG}
          onConfirm={() => {
            setShowSheetDirtyConfirm(false);
            handleClose();
          }}
          onClose={() => setShowSheetDirtyConfirm(false)}
        />
      )}
    </>
  );
}

/**
 * 취소 버튼 — 데스크톱(base Modal)에서는 base의 닫기 요청([[useModalRequestClose]])으로 가 dirty 확인을
 * 거친다. 그 context는 Modal children **하위 컴포넌트**에서만 잡히므로 별 컴포넌트로 둔다.
 * BottomSheet 분기에는 context가 없어 `fallbackClose`(시트의 확인 경로)로 떨어진다.
 */
function CancelButton({ disabled, fallbackClose }: { disabled: boolean; fallbackClose: () => void }) {
  const requestClose = useModalRequestClose();
  return (
    <button
      type="button"
      onClick={requestClose ?? fallbackClose}
      disabled={disabled}
      className="flex-1 py-3 px-4 border border-line rounded-card-sm text-ink-700 font-medium hover:bg-surface-hover disabled:opacity-50"
    >
      취소
    </button>
  );
}

export default AddFindingSheet;
